/**
 * Kanban Service
 * 
 * Servizio per le operazioni Kanban board - spostamento task tra colonne
 * Integra con ProjectTaskController per le operazioni di movimento
 */

import axiosInstance from '../lib/axios-client';
import { KanbanColumn, UserRole } from '../types/api';

const API_URL = '/api/ProjectTask';

// Funzione per ottenere il ruolo utente corrente (da implementare con il sistema auth reale)
const getCurrentUserRole = (): UserRole => {
  // TODO: Implementare con il sistema di autenticazione reale
  // Per ora restituiamo Admin come fallback
  const userRole = localStorage.getItem('userRole');
  if (userRole) {
    return parseInt(userRole) as UserRole;
  }
  return UserRole.Admin; // Fallback temporaneo
};

// Funzione per filtrare i task in base ai permessi del ruolo utente
const filterTasksByPermissions = (tasks: any[], userRole: UserRole): any[] => {
  switch (userRole) {
    case UserRole.Admin:
    case UserRole.CapoUfficioStile:
      // Admin e Capo Ufficio Stile possono vedere tutti i task
      return tasks;
      
    case UserRole.UfficioStile:
      // Ufficio Stile può vedere solo i task assegnati a lui o non assegnati
      return tasks.filter(task => {
        // Se il task non ha assegnatari o l'utente corrente è tra gli assegnatari
        return !task.assignedUsers || 
               task.assignedUsers.length === 0 ||
               task.assignedUsers.some((user: any) => user.role === 'UfficioStile');
      });
      
    case UserRole.CapoIndustrializzazione:
    case UserRole.Industrializzazione:
      // Industrializzazione può vedere solo i task del proprio reparto
      return tasks.filter(task => {
        return !task.assignedUsers || 
               task.assignedUsers.length === 0 ||
               task.assignedUsers.some((user: any) => 
                 user.role === 'Industrializzazione' || user.role === 'CapoIndustrializzazione'
               );
      });
      
    default:
      // Ruolo non riconosciuto: nessun task visibile
      return [];
  }
};

export interface MoveTaskToColumnRequest {
  newColumn: KanbanColumn;
  newOrder?: number;
}

export interface TaskOrderDto {
  taskId: number;
  order: number;
  column?: KanbanColumn;
}

export interface KanbanBoardData {
  columns: {
    [key in KanbanColumn]: {
      id: KanbanColumn;
      title: string;
      tasks: any[]; // Task[]
    }
  };
  totalTasks: number;
}

export const kanbanService = {
  
  /**
   * Sposta un task in una colonna Kanban
   */
  moveTaskToColumn: async (taskId: number, columnData: MoveTaskToColumnRequest): Promise<void> => {
    try {
      await axiosInstance.post(`${API_URL}/${taskId}/move-to-column`, columnData);
    } catch (error) {
      console.error(`Errore nello spostamento del task ${taskId} alla colonna ${columnData.newColumn}:`, error);
      throw error;
    }
  },
  
  /**
   * Riordina i task nel Kanban board
   */
  reorderTasks: async (taskOrders: TaskOrderDto[]): Promise<void> => {
    try {
      await axiosInstance.post(`${API_URL}/reorder`, taskOrders);
    } catch (error) {
      console.error('Errore nel riordinamento dei task:', error);
      throw error;
    }
  },
  
  /**
   * Ottiene la struttura del Kanban board con i task organizzati per colonna
   */
  getKanbanBoard: async (requestId?: string): Promise<KanbanBoardData> => {
    try {
      // Se abbiamo un requestId specifico, filtriamo per quello
      const endpoint = requestId ? `${API_URL}/byRequest/${requestId}` : API_URL;
      
      // Aggiungiamo parametri per recuperare tutti i task (gestione paginazione)
      const params = {
        page: 1,
        pageSize: 1000, // Numero alto per recuperare tutti i task
        // Altri filtri possono essere aggiunti qui
      };
      
      const response = await axiosInstance.get(endpoint, { params });
      
      // Gestione risposta paginata - supporta sia array diretto che oggetto paginato
      let tasks = response.data.data.items;
      

      // Applica filtri basati sui permessi dell'utente corrente
      const userRole = getCurrentUserRole();
      tasks = filterTasksByPermissions(tasks, userRole);

      const columns: KanbanBoardData['columns'] = {
        [KanbanColumn.ToDo]: {
          id: KanbanColumn.ToDo,
          title: 'Da Fare',
          tasks: []
        },
        [KanbanColumn.InProgress]: {
          id: KanbanColumn.InProgress,
          title: 'In Corso',
          tasks: []
        },
        [KanbanColumn.Done]: {
          id: KanbanColumn.Done,
          title: 'Completato',
          tasks: []
        }
      };
      
      // Mappiamo i task alle rispettive colonne basandoci sullo status
      tasks.forEach((task: any) => {
        // Skip null or undefined tasks
        if (!task) return;
        
        let kanbanColumn: KanbanColumn;
        
        // Mappiamo lo status del task alla colonna Kanban appropriata
        // Prova prima statusString poi status come fallback
        const taskStatus = (task.statusString || task.status || 'ToDo').toLowerCase();
        switch (taskStatus) {
          case 'ideas':
          case 'todo':
            kanbanColumn = KanbanColumn.ToDo;
            break;
          case 'doing':
          case 'inprogress':
          case 'testing':
            kanbanColumn = KanbanColumn.InProgress;
            break;
          case 'done':
          case 'completed':
            kanbanColumn = KanbanColumn.Done;
            break;
          default:
            kanbanColumn = KanbanColumn.ToDo;
        }
        
        // Aggiungiamo il task alla colonna appropriata
        if (columns[kanbanColumn]) {
          columns[kanbanColumn].tasks.push({
            ...task,
            kanbanColumn,
            // Aggiungiamo informazioni utili per l'UI
            completionPercentage: task.completionPercentage || 0,
            checklistItemsCount: task.checklistItemsCount || 0,
            completedChecklistItemsCount: task.completedChecklistItemsCount || 0,
            isOverdue: task.isOverdue || false
          });
        }
      });
      
      // Ordiniamo i task in ogni colonna per posizione
      Object.values(columns).forEach(column => {
        column.tasks.sort((a, b) => (a.position || 0) - (b.position || 0));
      });
      
      return {
        columns,
        totalTasks: tasks.length
      };
    } catch (error) {
      console.error('Errore nel recupero del Kanban board:', error);
      throw error;
    }
  },
  
  /**
   * Aggiorna la posizione di un task all'interno della stessa colonna
   */
  updateTaskPosition: async (taskId: number, newPosition: number): Promise<void> => {
    try {
      // Per ora utilizziamo l'endpoint di riordinamento con un singolo task
      await kanbanService.reorderTasks([{
        taskId,
        order: newPosition
      }]);
    } catch (error) {
      console.error(`Errore nell'aggiornamento della posizione del task ${taskId}:`, error);
      throw error;
    }
  },
  
  /**
   * Sposta un task da una colonna a un'altra con nuova posizione
   */
  moveTaskBetweenColumns: async (
    taskId: number, 
    sourceColumn: KanbanColumn, 
    targetColumn: KanbanColumn, 
    newPosition: number
  ): Promise<void> => {
    try {
      // Prima spostiamo il task nella nuova colonna
      await kanbanService.moveTaskToColumn(taskId, {
        newColumn: targetColumn,
        newOrder: newPosition
      });
    } catch (error) {
      console.error(`Errore nello spostamento del task ${taskId} da ${sourceColumn} a ${targetColumn}:`, error);
      throw error;
    }
  },
  
  /**
   * Ottiene le statistiche del Kanban board
   */
  getKanbanStatistics: async (requestId?: string): Promise<{
    totalTasks: number;
    tasksByColumn: { [key in KanbanColumn]: number };
    completionRate: number;
    averageTasksPerColumn: number;
  }> => {
    try {
      const board = await kanbanService.getKanbanBoard(requestId);
      
      const tasksByColumn = {
        [KanbanColumn.ToDo]: board.columns[KanbanColumn.ToDo].tasks.length,
        [KanbanColumn.InProgress]: board.columns[KanbanColumn.InProgress].tasks.length,
        [KanbanColumn.Done]: board.columns[KanbanColumn.Done].tasks.length
      };
      
      const completionRate = board.totalTasks > 0 
        ? (tasksByColumn[KanbanColumn.Done] / board.totalTasks) * 100 
        : 0;
        
      const averageTasksPerColumn = board.totalTasks / 3;
      
      return {
        totalTasks: board.totalTasks,
        tasksByColumn,
        completionRate: Math.round(completionRate * 100) / 100,
        averageTasksPerColumn: Math.round(averageTasksPerColumn * 100) / 100
      };
    } catch (error) {
      console.error('Errore nel calcolo delle statistiche Kanban:', error);
      throw error;
    }
  },
  
  /**
   * Utility per convertire TaskStatus in KanbanColumn
   */
  taskStatusToKanbanColumn: (status: string): KanbanColumn => {
    switch (status?.toLowerCase()) {
      case 'ideas':
      case 'todo':
        return KanbanColumn.ToDo;
      case 'doing':
      case 'inprogress':
      case 'testing':
        return KanbanColumn.InProgress;
      case 'done':
      case 'completed':
        return KanbanColumn.Done;
      default:
        return KanbanColumn.ToDo;
    }
  },
  
  /**
   * Utility per convertire KanbanColumn in TaskStatus
   */
  kanbanColumnToTaskStatus: (column: KanbanColumn): string => {
    switch (column) {
      case KanbanColumn.ToDo:
        return 'todo';
      case KanbanColumn.InProgress:
        return 'doing';
      case KanbanColumn.Done:
        return 'done';
      default:
        return 'todo';
    }
  },
  
  /**
   * Ottiene il titolo localizzato per una colonna Kanban
   */
  getColumnTitle: (column: KanbanColumn): string => {
    switch (column) {
      case KanbanColumn.ToDo:
        return 'Da Fare';
      case KanbanColumn.InProgress:
        return 'In Corso';
      case KanbanColumn.Done:
        return 'Completato';
      default:
        return 'Sconosciuto';
    }
  },
  
  /**
   * Ottiene il colore per una colonna Kanban
   */
  getColumnColor: (column: KanbanColumn): string => {
    switch (column) {
      case KanbanColumn.ToDo:
        return '#6b7280'; // gray-500
      case KanbanColumn.InProgress:
        return '#3b82f6'; // blue-500
      case KanbanColumn.Done:
        return '#10b981'; // emerald-500
      default:
        return '#6b7280';
    }
  },

  /**
   * Metodo di utilità per impostare il ruolo utente (da usare dopo il login)
   */
  setUserRole: (role: UserRole) => {
    localStorage.setItem('userRole', role.toString());
  },

  /**
   * Metodo di utilità per ottenere il ruolo utente corrente
   */
  getUserRole: (): UserRole => {
    return getCurrentUserRole();
  },
};

export default kanbanService;